<template>
  <div class="messageAdmin">
    <v-card>
      <v-card-title class="py-0">
        <h1 class="text-h2 mt-10 mb-7">
          {{ $t("infoMessagesStrings.pageTitle") }}
        </h1>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="messages"
          :no-data-text="$t('infoMessagesStrings.noTableData')"
          :no-results-text="$t('infoMessagesStrings.noTableResults')"
          :loading="loading"
        >
          <template #top>
            <v-toolbar
              flat
              color="white"
            >
              <info-message-edit-dialog
                :message="messageToEdit"
                @cancelled="onEditCancel"
                @add-new="onAddNew"
                @saved="onSaved"
              />
            </v-toolbar>
          </template>
          <template #item.displayFromDate="{ item }">
            {{ m(item.displayFromDate).format($t("commonStrings.dateDisplayFormat")) }}
          </template>
          <template #item.displayToDate="{ item }">
            {{ m(item.displayToDate).format($t("commonStrings.dateDisplayFormat")) }}
          </template>
          <template #item.editAction="{ item }">
            <v-btn
              size="small"
              rounded
              class="primary-inverted-button my-2"
              @click="onEdit(item)"
            >
              {{ $t("infoMessagesStrings.editButton") }}
            </v-btn>
            <v-dialog
              v-model="displayDeleteConfirm"
              max-width="500"
              persistent
            >
              <template #activator="{ props }">
                <v-btn
                  size="small"
                  rounded
                  class="accent-inverted-button my-2 ml-2"
                  v-bind="props"
                >
                  {{ $t("infoMessagesStrings.removeButton") }}
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  {{
                    $t("infoMessagesStrings.confirmDeleteTitle")
                  }}
                </v-card-title>
                <v-card-text>
                  {{
                    $t("infoMessagesStrings.confirmDeleteText")
                  }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    rounded
                    class="primary-inverted-button mb-2 mb-sm-0"
                    :block="xs"
                    size="small"
                    @click="displayDeleteConfirm = false"
                  >
                    {{ $t("commonStrings.cancel") }}
                  </v-btn>
                  <v-btn
                    rounded
                    class="accent-inverted-button ml-0 ml-sm-2"
                    :block="xs"
                    size="small"
                    @click="onDelete(item)"
                  >
                    {{ $t("infoMessagesStrings.removeButton") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { api, urls } from "@/utils/api";
import InfoMessageEditDialog from "./InfoMessageEditDialog";
import { handleError } from "@/utils/errorHandler";
import { useDisplay } from 'vuetify';
import { useSnacks } from '@/composables/snacks';
import { m } from '@/plugins/moment';

export default {
  name: "MessageAdminPage",
  components: { InfoMessageEditDialog },
  setup() {
    const { xs } = useDisplay();
    const { addSnackbar } = useSnacks();
    return { xs, addSnackbar, m };
  },
  data() {
    return {
      displayDeleteConfirm: false,
      messages: [],
      messageToEdit: {},
      headers: [
        {
          text: this.$t("infoMessagesStrings.headers.title"),
          value: "title"
        },
        {
          text: this.$t("infoMessagesStrings.headers.fromDate"),
          value: "displayFromDate"
        },
        {
          text: this.$t("infoMessagesStrings.headers.toDate"),
          value: "displayToDate"
        },
        {
          value: "editAction",
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      stateInitiated: "contextStore/initiated",
      urlState: "utilsRestStore/urlState"
    }),
    loading() {
      return this.urlState(urls.v1.infoMessages.get());
    }
  },
  methods: {
    ...mapActions({
      refetchInfoMessages: "infoMessagesStore/refetch"
    }),
    async fetch() {
      try {
        const response = await api.v1.infoMessages.get();

        if (response.status === 200 && response.data) {
          this.messages = response.data;
        }
      } catch (error) {
        await handleError(error);
      }
    },
    setDefaultMessage(id) {
      this.messageToEdit = {
        infoMessageId: id,
        title: "",
        messageText: "",
        infoMessageType: 0,
        readMoreUrl: "",
        displayFromDate: m().format(
          this.$t("commonStrings.dateValueFormat")
        ),
        displayToDate: m()
          .add(1, "month")
          .format(this.$t("commonStrings.dateValueFormat")),
        displayOnPage: "",
        recipientType: 0
      };
    },
    async onSaved() {
      this.setDefaultMessage(-1);
      await this.fetch();
      await this.refetchInfoMessages();
    },
    onEdit(message) {
      this.messageToEdit = message;
    },
    onAddNew() {
      this.setDefaultMessage(0);
    },
    onEditCancel() {
      this.setDefaultMessage(-1);
    },
    async onDelete(message) {
      try {
        const response = await api.v1.infoMessages.delete(message.infoMessageId);

        if (response.status === 204) {
          this.addSnackbar(this.$t("infoMessagesStrings.deleted"), "success");
          this.displayDeleteConfirm = false;
          await this.fetch();
          await this.refetchInfoMessages();
        }
      } catch (error) {
        await handleError(error);
      }
    }
  },
  watch: {
    stateInitiated: {
      handler: async function (val) {
        if (val) {
          await this.fetch();
        }
      },
      immediate: true
    }
  }
};
</script>
